import { useCallback, useEffect, useRef, useState } from "react";
import { useUpdateParticipationRequestNotesMutation } from "../../../../../autogen/bff-api";
import { useApiError } from "../../../../../common/errors/useApiError";

const DEBOUNCE_MS = 300;

export type NotesFieldStatus = "idle" | "pending" | "saving" | "saved" | "error";

interface FieldState {
  value: string;
  status: NotesFieldStatus;
}

type EditorValues = {
  internalNote: string;
  rejectionReason: string;
};

type FieldKey = keyof EditorValues;

const FIELDS: FieldKey[] = ["internalNote", "rejectionReason"];

export interface UseBuyerReviewNotesEditor {
  values: EditorValues;
  setField: (field: FieldKey, value: string) => void;
  statusFor: (field: FieldKey) => NotesFieldStatus;
  flush: () => Promise<void>;
}

interface ServerNotes {
  internalNote: string | null | undefined;
  rejectionReason: string | null | undefined;
}

// Debounced per-field PATCH for participation request notes (internalNote, rejectionReason).
// Mirrors the pending-map approach of useRequestQualificationReviewEditor but tracks per-field
// save status so the UI can show individual persistence indicators. The pending map always holds
// the latest unsaved value; a save that completes while a newer value is pending triggers a
// follow-up save rather than dropping the keystrokes.
export const useBuyerReviewNotesEditor = (
  eventId: string,
  requestId: string,
  serverNotes: ServerNotes,
): UseBuyerReviewNotesEditor => {
  const { trigger } = useApiError();
  const [updateNotes] = useUpdateParticipationRequestNotesMutation();
  const isMounted = useRef(true);

  const [fields, setFields] = useState<Record<FieldKey, FieldState>>(() => ({
    internalNote: { value: serverNotes.internalNote ?? "", status: "idle" },
    rejectionReason: { value: serverNotes.rejectionReason ?? "", status: "idle" },
  }));

  // Latest unsaved value per field — only removed when that exact value is being sent.
  const pendingValues = useRef<Partial<EditorValues>>({});
  // Active debounce timers per field.
  const flushTimers = useRef<Partial<Record<FieldKey, ReturnType<typeof setTimeout>>>>({});
  // In-flight save promises keyed by field — so flush() and follow-up saves can await them.
  const inFlightPromises = useRef<Partial<Record<FieldKey, Promise<void>>>>({});

  useEffect(() => {
    // Per-field guard: only sync a field from the server when that specific field has no
    // pending or in-flight save. This way a concurrent internalNote edit does not suppress
    // a server-pushed rejectionReason update (and vice versa).
    setFields((prev) => {
      let next = prev;
      for (const field of FIELDS) {
        const hasPending = pendingValues.current[field] !== undefined;
        const hasInFlight = inFlightPromises.current[field] !== undefined;
        if (hasPending || hasInFlight) continue;
        const serverValue = field === "internalNote" ? serverNotes.internalNote : serverNotes.rejectionReason;
        const normalized = serverValue ?? "";
        if (prev[field].value !== normalized || prev[field].status !== "idle") {
          next = { ...next, [field]: { value: normalized, status: "idle" } };
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverNotes.internalNote, serverNotes.rejectionReason]);

  // Saves the latest pending value for a field. If a save is already in flight, waits for it
  // and then re-checks the pending map, so a value typed mid-save is never dropped. Resolves
  // once the latest value at call time has been persisted (or failed).
  const saveFieldRef = useRef<(field: FieldKey) => Promise<void>>(() => Promise.resolve());
  const saveField = useCallback(
    (field: FieldKey): Promise<void> => {
      const existing = inFlightPromises.current[field];
      if (existing) {
        return existing.then(() => saveFieldRef.current(field));
      }

      const value = pendingValues.current[field];
      if (value === undefined) return Promise.resolve();
      delete pendingValues.current[field];

      if (isMounted.current) {
        setFields((prev) => ({ ...prev, [field]: { ...prev[field], status: "saving" } }));
      }

      const promise: Promise<void> = updateNotes({
        eventId,
        requestId,
        buyerReviewNotesPatchDto: { [field]: value },
      })
        .unwrap()
        .then(() => {
          if (!isMounted.current) return;
          setFields((prev) =>
            // Don't clobber a newer "pending" status from keystrokes typed during the save.
            prev[field].status === "saving"
              ? { ...prev, [field]: { ...prev[field], status: "saved" } }
              : prev,
          );
        })
        .catch((e: unknown) => {
          if (!isMounted.current) return;
          setFields((prev) =>
            prev[field].status === "saving"
              ? { ...prev, [field]: { ...prev[field], status: "error" } }
              : prev,
          );
          trigger(e);
        })
        .finally(() => {
          delete inFlightPromises.current[field];
          // A newer value arrived while this save was in flight — send it now.
          if (pendingValues.current[field] !== undefined) {
            void saveFieldRef.current(field);
          }
        });

      inFlightPromises.current[field] = promise;
      return promise;
    },
    [eventId, requestId, trigger, updateNotes],
  );
  saveFieldRef.current = saveField;

  const setField = useCallback((field: FieldKey, value: string) => {
    setFields((prev) => ({ ...prev, [field]: { value, status: "pending" } }));
    pendingValues.current[field] = value;
    if (flushTimers.current[field]) clearTimeout(flushTimers.current[field]);
    flushTimers.current[field] = setTimeout(() => {
      delete flushTimers.current[field];
      void saveFieldRef.current(field);
    }, DEBOUNCE_MS);
  }, []);

  const flush = useCallback(async () => {
    for (const field of FIELDS) {
      const timer = flushTimers.current[field];
      if (timer) {
        clearTimeout(timer);
        delete flushTimers.current[field];
      }
    }
    await Promise.all(FIELDS.map((field) => saveField(field)));
  }, [saveField]);

  // Flush on unmount to avoid losing unsaved keystrokes.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      void flushRef.current();
    };
  }, []);

  const statusFor = useCallback(
    (field: FieldKey): NotesFieldStatus => fields[field].status,
    [fields],
  );

  return {
    values: {
      internalNote: fields.internalNote.value,
      rejectionReason: fields.rejectionReason.value,
    },
    setField,
    statusFor,
    flush,
  };
};
